/** @module @liquidefy/code-editor/git/git-repository @version 2.0.0 @license LQD Research LLC */

import type {
  EditorResult,
  GitRepositoryState,
  GitBranch,
  GitTag,
  GitStash,
  GitFileStatus,
  GitStatusCode,
  GitConfig,
  BranchName,
  TagName,
  StashId,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTagName,
  asStashId,
  now,
} from '../../code-editor.js';

// ═══ §1 REPOSITORY PREDICATES — clean, uncommitted, untracked, summary ═══

export function updateConfig(
  state: GitRepositoryState,
  config: GitConfig
): EditorResult<GitRepositoryState> {
  return ok({ ...state, config }, 'CONFIG_UPDATED', 'Updated repository config');
}

export function isRepositoryClean(state: GitRepositoryState): boolean {
  return state.status.length === 0;
}

export function hasUncommittedChanges(state: GitRepositoryState): boolean {
  return state.status.some(
    (s) => s.indexStatus !== 'unmodified' || s.workTreeStatus !== 'unmodified'
  );
}

export function hasUntrackedFiles(state: GitRepositoryState): boolean {
  return state.status.some((s) => s.workTreeStatus === 'untracked');
}

export function repositorySummary(
  state: GitRepositoryState
): { branch: string; status: string; aheadBehind: string; stashCount: number } {
  const branch = state.head.type === 'branch' ? (state.head.name ?? 'unknown') : `detached@${(state.head.commitHash as string).slice(0, 7)}`;
  const staged = state.status.filter((s) => s.indexStatus !== 'unmodified' && s.indexStatus !== 'untracked').length;
  const unstaged = state.status.filter((s) => s.workTreeStatus !== 'unmodified' && s.workTreeStatus !== 'untracked').length;
  const untracked = state.status.filter((s) => s.workTreeStatus === 'untracked').length;
  const parts: string[] = [];
  if (staged > 0) parts.push(`${staged} staged`);
  if (unstaged > 0) parts.push(`${unstaged} unstaged`);
  if (untracked > 0) parts.push(`${untracked} untracked`);
  const status = parts.length > 0 ? parts.join(', ') : 'clean';
  const currentBranch = state.branches.find((b) => b.isHead);
  const ahead = currentBranch?.ahead ?? 0;
  const behind = currentBranch?.behind ?? 0;
  let aheadBehind = '';
  if (ahead > 0 && behind > 0) aheadBehind = `+${ahead} -${behind}`;
  else if (ahead > 0) aheadBehind = `+${ahead}`;
  else if (behind > 0) aheadBehind = `-${behind}`;
  else aheadBehind = 'up to date';
  return { branch, status, aheadBehind, stashCount: state.stashes.length };
}

// ═══ §3 BRANCH PREDICATES — existence, detached head, sort ═══

export function branchExists(state: GitRepositoryState, name: BranchName): boolean {
  return state.branches.some((b) => b.name === name);
}

export function isDetachedHead(state: GitRepositoryState): boolean {
  return state.head.type === 'detached';
}

export function sortBranches(
  branches: GitBranch[],
  by: 'name' | 'date' | 'ahead-behind'
): GitBranch[] {
  const sorted = [...branches];
  switch (by) {
    case 'name':
      sorted.sort((a, b) => (a.name as string).localeCompare(b.name as string));
      break;
    case 'date':
      sorted.sort((a, b) => (a.name as string).localeCompare(b.name as string));
      break;
    case 'ahead-behind':
      sorted.sort((a, b) => (b.ahead + b.behind) - (a.ahead + a.behind));
      break;
  }
  return sorted;
}

// ═══ §5 TAG MANAGEMENT — add, remove, query, sort tags ═══

export function addTag(state: GitRepositoryState, tag: GitTag): EditorResult<GitRepositoryState> {
  const exists = state.tags.some((t) => t.name === tag.name);
  if (exists) return err('TAG_EXISTS', `Tag "${tag.name}" already exists`);
  return ok(
    { ...state, tags: [...state.tags, tag] },
    'TAG_ADDED',
    `Added tag "${tag.name}"`
  );
}

export function removeTag(state: GitRepositoryState, name: TagName): EditorResult<GitRepositoryState> {
  const filtered = state.tags.filter((t) => t.name !== name);
  if (filtered.length === state.tags.length) return err('TAG_NOT_FOUND', `Tag "${name}" not found`);
  return ok({ ...state, tags: filtered }, 'TAG_REMOVED', `Removed tag "${name}"`);
}

export function getTag(state: GitRepositoryState, name: TagName): GitTag | undefined {
  return state.tags.find((t) => t.name === name);
}

export function getAnnotatedTags(state: GitRepositoryState): GitTag[] {
  return state.tags.filter((t) => t.isAnnotated);
}

export function getLightweightTags(state: GitRepositoryState): GitTag[] {
  return state.tags.filter((t) => !t.isAnnotated);
}

export function sortTags(tags: GitTag[], by: 'name' | 'date'): GitTag[] {
  const sorted = [...tags];
  switch (by) {
    case 'name':
      sorted.sort((a, b) => (a.name as string).localeCompare(b.name as string));
      break;
    case 'date':
      sorted.sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
      break;
  }
  return sorted;
}

export function latestTag(state: GitRepositoryState): GitTag | undefined {
  if (state.tags.length === 0) return undefined;
  return sortTags(state.tags, 'date')[0];
}

// ═══ §6 STASH MANAGEMENT — add, remove, clear, query stashes ═══

export function addStash(
  state: GitRepositoryState,
  message: string,
  branch?: BranchName
): EditorResult<GitRepositoryState> {
  const stash: GitStash = {
    id: asStashId(generateId('stash')),
    message: message || 'WIP',
    branch: branch ?? (state.head.type === 'branch' ? state.head.name : undefined),
    date: now(),
  };
  return ok(
    { ...state, stashes: [stash, ...state.stashes] },
    'STASH_ADDED',
    `Stashed changes: "${stash.message}"`
  );
}

export function removeStash(
  state: GitRepositoryState,
  index: number
): EditorResult<GitRepositoryState> {
  if (index < 0 || index >= state.stashes.length) {
    return err('STASH_NOT_FOUND', `Stash @{${index}} not found`);
  }
  const stashes = [...state.stashes.slice(0, index), ...state.stashes.slice(index + 1)];
  return ok({ ...state, stashes }, 'STASH_REMOVED', `Removed stash @{${index}}`);
}

export function clearStashes(state: GitRepositoryState): EditorResult<GitRepositoryState> {
  const count = state.stashes.length;
  return ok({ ...state, stashes: [] }, 'STASHES_CLEARED', `Cleared ${count} stash(es)`);
}

export function stashCount(state: GitRepositoryState): number {
  return state.stashes.length;
}

export function getStash(state: GitRepositoryState, index: number): GitStash | undefined {
  if (index < 0 || index >= state.stashes.length) return undefined;
  return state.stashes[index];
}

export function stashLabel(stash: GitStash): string {
  const branchPart = stash.branch ? ` on ${stash.branch}` : '';
  return `stash${branchPart}: ${stash.message}`;
}

// ═══ §7 STATUS PARSING — parse git status output into structured data ═══

const STATUS_CODE_MAP: Record<string, GitStatusCode> = {
  ' ': 'unmodified',
  'M': 'modified',
  'A': 'added',
  'D': 'deleted',
  'R': 'renamed',
  'C': 'copied',
  '?': 'untracked',
  '!': 'ignored',
  'U': 'unmerged',
};

export function parseStatusLine(line: string): EditorResult<GitFileStatus> {
  if (line.length < 4) return err('INVALID_STATUS_LINE', `Status line too short: "${line}"`);
  const indexChar = line.charAt(0);
  const workTreeChar = line.charAt(1);
  const path = line.substring(3).trim();
  if (!path) return err('EMPTY_PATH', 'Status line has no file path');
  const indexStatus: GitStatusCode = STATUS_CODE_MAP[indexChar] ?? 'unmodified';
  const workTreeStatus: GitStatusCode = STATUS_CODE_MAP[workTreeChar] ?? 'unmodified';
  const status: GitFileStatus = {
    path,
    indexStatus,
    workTreeStatus,
    isSubmodule: false,
  };
  return ok(status, 'STATUS_PARSED', `Parsed status for "${path}"`);
}

export function parseStatusOutput(output: string): EditorResult<GitFileStatus[]> {
  const lines = output.split('\n').filter((l) => l.length >= 3);
  const statuses: GitFileStatus[] = [];
  for (const line of lines) {
    const result = parseStatusLine(line);
    if (result.success) {
      statuses.push(result.value);
    }
  }
  return ok(statuses, 'STATUS_OUTPUT_PARSED', `Parsed ${statuses.length} status entries`);
}

export function categorizeStatus(
  statuses: GitFileStatus[]
): { staged: GitFileStatus[]; unstaged: GitFileStatus[]; untracked: GitFileStatus[]; conflicted: GitFileStatus[] } {
  const staged: GitFileStatus[] = [];
  const unstaged: GitFileStatus[] = [];
  const untracked: GitFileStatus[] = [];
  const conflicted: GitFileStatus[] = [];
  for (const s of statuses) {
    if (s.indexStatus === 'unmerged' || s.workTreeStatus === 'unmerged') {
      conflicted.push(s);
    } else if (s.workTreeStatus === 'untracked') {
      untracked.push(s);
    } else {
      if (s.indexStatus !== 'unmodified') staged.push(s);
      if (s.workTreeStatus !== 'unmodified') unstaged.push(s);
    }
  }
  return { staged, unstaged, untracked, conflicted };
}

export function statusSummary(
  statuses: GitFileStatus[]
): { modified: number; added: number; deleted: number; renamed: number; untracked: number } {
  let modified = 0, added = 0, deleted = 0, renamed = 0, untracked = 0;
  for (const s of statuses) {
    const code = s.indexStatus !== 'unmodified' ? s.indexStatus : s.workTreeStatus;
    switch (code) {
      case 'modified': modified++; break;
      case 'added': added++; break;
      case 'deleted': deleted++; break;
      case 'renamed': renamed++; break;
      case 'untracked': untracked++; break;
    }
  }
  return { modified, added, deleted, renamed, untracked };
}

export function hasConflicts(statuses: GitFileStatus[]): boolean {
  return statuses.some((s) => s.indexStatus === 'unmerged' || s.workTreeStatus === 'unmerged');
}

export function conflictedFiles(statuses: GitFileStatus[]): GitFileStatus[] {
  return statuses.filter((s) => s.indexStatus === 'unmerged' || s.workTreeStatus === 'unmerged');
}
